import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { adminUpdateCertificateStatus } from "../../../../api";
import ImageModal from "../../../../components/Modals/ImageModal";
import { colors } from "../../../../styles";
import { ICertificate } from "../../../../types";
import { formatDateTimeKor } from "../../../../utils";
import { FieldRow, ImageThumb, ObjectStatus, StatusSelect, ValiditySelect } from "./shared";

interface ICertificateDraft {
    imageIsValid: boolean;
    status: ObjectStatus;
    rejectedReason: string;
}

interface ICertificateModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    certificate: ICertificate;
}

const toDraft = (certificate: ICertificate): ICertificateDraft => ({
    imageIsValid: certificate.imageIsValid,
    status: certificate.status,
    rejectedReason: certificate.rejectedReason ?? "",
});

export default function CertificateModal({ isOpen, onClose, userId, certificate }: ICertificateModalProps) {
    const toast = useToast();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<ICertificateDraft>(() => toDraft(certificate));
    const { isOpen: isImageOpen, onOpen: onOpenImage, onClose: onCloseImage } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    /* 모달이 열릴 때마다 서버 값으로 초안을 맞춘다. */
    useEffect(() => {
        if (isOpen) setDraft(toDraft(certificate));
    }, [isOpen, certificate]);

    const isDirty = useMemo(() => {
        const original = toDraft(certificate);
        return JSON.stringify(original) !== JSON.stringify(draft);
    }, [certificate, draft]);

    const allValid = draft.imageIsValid;

    const updateMutation = useMutation({
        mutationFn: adminUpdateCertificateStatus,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminGetUsers"] });
            toast({ title: "재학 증명 정보가 저장되었습니다.", status: "success", position: "top", duration: 2000, isClosable: true });
            onClose();
        },
        onError: () => {
            toast({ title: "재학 증명 정보 저장에 실패했습니다.", status: "error", position: "top", duration: 2000, isClosable: true });
        },
    });

    const onSave = () => {
        updateMutation.mutate({
            userId,
            certificateId: certificate.id,
            status: draft.status,
            rejectedReason: draft.rejectedReason.trim() || null,
            imageIsValid: draft.imageIsValid,
        });
    };

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImage();
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text fontSize={20} fontWeight={700} color={colors.black}>재학증명서</Text>
                        <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody>
                        <Stack spacing={1} width="100%">
                            <FieldRow label="재학 증명서">
                                <ImageThumb url={certificate.image} alt="certificate" onClick={openImage} />
                            </FieldRow>
                            <FieldRow label="재학 증명서 유효성">
                                <ValiditySelect
                                    value={draft.imageIsValid}
                                    onChange={(imageIsValid) => setDraft((prev) => ({ ...prev, imageIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="상태">
                                <StatusSelect
                                    value={draft.status}
                                    rejectDisabled={allValid}
                                    onChange={(status) => {
                                        if (allValid && status === "REJECTED") return;
                                        setDraft((prev) => ({ ...prev, status }));
                                    }}
                                />
                            </FieldRow>
                            <FieldRow label="거절 사유">
                                <Input
                                    size="sm"
                                    value={draft.rejectedReason}
                                    placeholder="거절 사유"
                                    onChange={(event) => setDraft((prev) => ({ ...prev, rejectedReason: event.target.value }))}
                                />
                            </FieldRow>
                            <FieldRow label="생성일">
                                <Text fontSize={14}>{formatDateTimeKor(String(certificate.createdAt))}</Text>
                            </FieldRow>
                            <FieldRow label="수정일">
                                <Text fontSize={14}>{formatDateTimeKor(String(certificate.updatedAt))}</Text>
                            </FieldRow>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            width="100%"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            isDisabled={!isDirty}
                            isLoading={updateMutation.isPending}
                            onClick={onSave}
                        >
                            저장
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {selectedImageUrl && (
                <ImageModal isOpen={isImageOpen} onClose={onCloseImage} imageUrl={selectedImageUrl} />
            )}
        </>
    );
}
